import { HTMLAttributes, createElement, ReactNode } from "react";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type TextVariant = "body" | "caption" | "small" | "code";
type TextWeight = "normal" | "medium" | "semibold" | "bold";
type TextAlign = "left" | "center" | "right";

interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level: HeadingLevel;
  as?: `h${HeadingLevel}`;
  weight?: TextWeight;
  align?: TextAlign;
  children?: ReactNode;
}

interface TextProps extends HTMLAttributes<HTMLParagraphElement> {
  variant?: TextVariant;
  weight?: TextWeight;
  align?: TextAlign;
  muted?: boolean;
  children?: ReactNode;
}

const headingStyles: Record<HeadingLevel, string> = {
  1: "text-4xl md:text-5xl leading-tight tracking-tight",
  2: "text-3xl md:text-4xl leading-tight tracking-tight",
  3: "text-2xl md:text-3xl leading-snug",
  4: "text-xl md:text-2xl leading-snug",
  5: "text-lg md:text-xl leading-normal",
  6: "text-base md:text-lg leading-normal",
};

const textVariantStyles: Record<TextVariant, string> = {
  body: "text-base leading-relaxed",
  caption: "text-sm leading-normal",
  small: "text-xs leading-normal",
  code: "text-sm font-mono bg-[var(--color-code-bg)] text-[var(--color-code-text)] px-1.5 py-0.5 rounded",
};

const weightStyles: Record<TextWeight, string> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

const alignStyles: Record<TextAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function Heading({
  level,
  as,
  weight = "bold",
  align = "left",
  className = "",
  children,
  ...props
}: HeadingProps) {
  const tag = as || `h${level}`;

  return createElement(
    tag,
    {
      className: `${headingStyles[level]} ${weightStyles[weight]} ${alignStyles[align]} text-[var(--color-text-primary)] keep-all ${className}`,
      ...props,
    },
    children
  );
}

export function Text({
  variant = "body",
  weight = "normal",
  align = "left",
  muted = false,
  className = "",
  children,
  ...props
}: TextProps) {
  // code variant는 인라인 요소로 렌더링
  if (variant === "code") {
    return (
      <code
        className={`${textVariantStyles[variant]} ${className}`}
        {...(props as HTMLAttributes<HTMLElement>)}
      >
        {children}
      </code>
    );
  }

  return (
    <p
      className={`${textVariantStyles[variant]} ${weightStyles[weight]} ${alignStyles[align]} ${muted ? "text-[var(--color-text-muted)]" : "text-[var(--color-text-primary)]"} keep-all ${className}`}
      {...props}
    >
      {children}
    </p>
  );
}
